import { HeartHandshakeIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  EmotionAnalysisShape,
  emotionAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  EmotionAnalysisProps,
  emotionAnalysisCategories,
  EmotionAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isEmotionAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is EmotionAnalysisShape {
  return shape.type === emotionAnalysisType;
}

export class EmotionAnalysisTool extends ToolStateNode {
  static override id = emotionAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: emotionAnalysisType,
    icon: HeartHandshakeIcon,
    label: EmotionAnalysisShapeUtil.docs.title,
    defaultColor: EmotionAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(emotionAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={EmotionAnalysis}
        util={EmotionAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: emotionAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class EmotionAnalysisShapeUtil extends BindableUtil<EmotionAnalysisShape> {
  static override migrations = createMigrations(emotionAnalysisType);
  static override type = emotionAnalysisType;

  static override docs = {
    defaultColor: "purple" as const,
    title: "Emotion Analysis",
    description:
      "Use AI to analyze the emotional content of posts. This detects a wide range of emotions from joy and love to anger and sadness.",
  };

  override getType() {
    return emotionAnalysisType;
  }

  static override props: RecordProps<EmotionAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    emotionAnalysisCategory: T.literalEnum(...emotionAnalysisCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      emotionAnalysisCategory: "Joy" as const,
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: EmotionAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <EmotionAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function EmotionAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: EmotionAnalysisShape;
  className?: string;
  updateShape: (
    shape: EmotionAnalysisShape,
    props: Partial<EmotionAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || "pink"}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={EmotionAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Emotion Analysis Category"
            options={emotionAnalysisCategories}
            value={shape.props.emotionAnalysisCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                emotionAnalysisCategory: v as EmotionAnalysisCategory,
              })
            }
            tooltip={EmotionAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
